import { LoaderFunction } from 'react-router-dom';
import { store } from '../../../../state/configureStore';
import { client } from '../../../../gql/client';
import { FETCH_ACTIONS } from '../../../../gql/queries/actions/actions';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { selectActionOrders } from '../../../../gql/selectors/actions';
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';
import { FETCH_USERS } from '../../../../gql/queries/users';
import { FETCH_MATERIALS } from '../../../../gql/queries/actions/materials';

export const HistoricalActionsReportLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
    void refreshOrderFlags(userData);
    try {
      if (!userData?.user) return {};

      const cursorActions = getCursor(tableIds.REPORTING_ACTIONS)(
        store.getState()
      );
      const dispatch = store.dispatch;
      const roleActive = userData.user.user.rolActive;

      // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
      let effectiveConcessionId = cursorActions?.concessionId;
      if (!effectiveConcessionId) {
        try {
          const {
            data: { concessions },
          } = await client.query({ query: GET_CONCESSIONS });
          if (concessions?.length === 1) {
            effectiveConcessionId = concessions[0].id;
            dispatch(
              setCursor(tableIds.REPORTING_ACTIONS, {
                ...cursorActions,
                concessionId: effectiveConcessionId,
              })
            );
          }
        } catch {
          /* continue without concessionId */
        }
      }

      // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
      // anterior interrumpido.
      dispatch(loadingData(tableIds.REPORTING_ACTIONS, false));

      const readActions = client.readQuery({
        query: FETCH_ACTIONS,
        variables: {
          input: {
            take: cursorActions?.take,
            cursor: cursorActions?.cursor,
            sortField: cursorActions?.sortField,
            orderBy: cursorActions?.orderBy,
            concessionId: effectiveConcessionId,
            fromDate: cursorActions?.fromDate,
            toDate: cursorActions?.toDate,
            indexExpedition: cursorActions?.indexExpedition,
            indexType: cursorActions?.indexType,
            classification: cursorActions?.classification,
            predefinedOrderId: cursorActions?.predefinedOrderId,
            states: cursorActions?.states,
            userIds: cursorActions?.userIds,
            operatorIds: cursorActions?.operatorIds,
            teamIds: cursorActions?.teamIds,
            capitolId: cursorActions?.capitolId,
            subCapitolId: cursorActions?.subCapitolId,
            externalCompanyId: cursorActions?.externalCompanyId,
            pkInitKm: cursorActions?.pkInitKm,
            pkInitMeter: cursorActions?.pkInitMeter,
            pkEndKm: cursorActions?.pkEndKm,
            pkEndMeter: cursorActions?.pkEndMeter,
            description: cursorActions?.description,
            laneCutting: cursorActions?.laneCutting,
            materialIds: cursorActions?.materialIds,
            hasMaterials: cursorActions?.hasMaterials,
            track: cursorActions?.track,
            activityId: cursorActions?.activityId,
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            onlyFlagged: cursorActions?.onlyFlagged,
          },
        },
      });

      const pageInfo = readActions?.actions?.pageInfo;
      const totalCount = readActions?.actions?.totalCount;

      const [
        {
          data: { capitols },
        },
        {
          data: { concessions },
        },
        {
          data: { subCapitols },
        },
        {
          data: { classifications },
        },
        {
          data: { externalCompanies },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
      ] = await Promise.all([
        client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_EXTERNAL_COMPANIES,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      ]);

      const filtersDataOptions = {
        // Opciones del filtro de materiales. Sale del catálogo ya cacheado

        // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

        // sin cobertura no tumba el loader.

        materials: await client
          .query({ query: FETCH_MATERIALS })
          .then((r: any) => r.data?.materials ?? [])
          .catch(() => []),
        capitols,
        concessions,
        subCapitols,
        classifications,
        externalCompanies,
        predefinedOrders,
        users,
      };

      const isCacheEmpty = JSON.stringify(readActions) === '{}';

      const actions =
        readActions &&
        !isCacheEmpty &&
        (await selectActionOrders(readActions?.actions?.actions));

      const combinedData = {
        actions,
        totalCount,
        pageInfo,
        filtersDataOptions,
        roleActive,
      };

      if (!readActions || isCacheEmpty) {
        dispatch(loadingData(tableIds.REPORTING_ACTIONS, true));
        try {
          let data;
          if (isCacheEmpty) {
            // Tarea #11: respetar el take guardado en cursor (page-size selector).
            const effectiveTake = firstPageTake(cursorActions?.take, takeBase);
            data = await client.query({
              query: FETCH_ACTIONS,
              variables: {
                input: {
                  take: effectiveTake,
                  cursor: '',
                  sortField: cursorActions?.sortField,
                  orderBy: cursorActions?.orderBy ?? 'desc',
                  concessionId: effectiveConcessionId,
                  fromDate: cursorActions?.fromDate,
                  toDate: cursorActions?.toDate,
                  indexExpedition: cursorActions?.indexExpedition,
                  indexType: cursorActions?.indexType,
                  classification: cursorActions?.classification,
                  predefinedOrderId: cursorActions?.predefinedOrderId,
                  states: cursorActions?.states,
                  userIds: cursorActions?.userIds,
                  operatorIds: cursorActions?.operatorIds,
                  teamIds: cursorActions?.teamIds,
                  capitolId: cursorActions?.capitolId,
                  subCapitolId: cursorActions?.subCapitolId,
                  externalCompanyId: cursorActions?.externalCompanyId,
                  pkInitKm: cursorActions?.pkInitKm,
                  pkInitMeter: cursorActions?.pkInitMeter,
                  pkEndKm: cursorActions?.pkEndKm,
                  pkEndMeter: cursorActions?.pkEndMeter,
                  description: cursorActions?.description,
                  laneCutting: cursorActions?.laneCutting,
                  materialIds: cursorActions?.materialIds,
                  hasMaterials: cursorActions?.hasMaterials,
                  track: cursorActions?.track,
                  activityId: cursorActions?.activityId,
                  onlyFlagged: cursorActions?.onlyFlagged,
                },
              },
            });

            const setCursorData = {
              take: effectiveTake,
              cursor: '',
              concessionId: effectiveConcessionId,
              fromDate: cursorActions?.fromDate,
              toDate: cursorActions?.toDate,
              indexExpedition: cursorActions?.indexExpedition,
              indexType: cursorActions?.indexType,
              classification: cursorActions?.classification,
              predefinedOrderId: cursorActions?.predefinedOrderId,
              states: cursorActions?.states,
              userIds: cursorActions?.userIds,
              operatorIds: cursorActions?.operatorIds,
              teamIds: cursorActions?.teamIds,
              capitolId: cursorActions?.capitolId,
              subCapitolId: cursorActions?.subCapitolId,
              externalCompanyId: cursorActions?.externalCompanyId,
              pkInitKm: cursorActions?.pkInitKm,
              pkInitMeter: cursorActions?.pkInitMeter,
              pkEndKm: cursorActions?.pkEndKm,
              pkEndMeter: cursorActions?.pkEndMeter,
              description: cursorActions?.description,
              laneCutting: cursorActions?.laneCutting,
              materialIds: cursorActions?.materialIds,
              hasMaterials: cursorActions?.hasMaterials,
              track: cursorActions?.track,
              activityId: cursorActions?.activityId,
              onlyFlagged: cursorActions?.onlyFlagged,
            };

            dispatch(setCursor(tableIds.REPORTING_ACTIONS, setCursorData));
          } else {
            data = await client.query({
              query: FETCH_ACTIONS,
              variables: {
                input: {
                  take: cursorActions?.take,
                  cursor: cursorActions?.cursor,
                  sortField: cursorActions?.sortField,
                  orderBy: cursorActions?.orderBy,
                  concessionId: effectiveConcessionId,
                  fromDate: cursorActions?.fromDate,
                  toDate: cursorActions?.toDate,
                  indexExpedition: cursorActions?.indexExpedition,
                  indexType: cursorActions?.indexType,
                  classification: cursorActions?.classification,
                  predefinedOrderId: cursorActions?.predefinedOrderId,
                  states: cursorActions?.states,
                  userIds: cursorActions?.userIds,
                  operatorIds: cursorActions?.operatorIds,
                  teamIds: cursorActions?.teamIds,
                  capitolId: cursorActions?.capitolId,
                  subCapitolId: cursorActions?.subCapitolId,
                  externalCompanyId: cursorActions?.externalCompanyId,
                  pkInitKm: cursorActions?.pkInitKm,
                  pkInitMeter: cursorActions?.pkInitMeter,
                  pkEndKm: cursorActions?.pkEndKm,
                  pkEndMeter: cursorActions?.pkEndMeter,
                  description: cursorActions?.description,
                  laneCutting: cursorActions?.laneCutting,
                  materialIds: cursorActions?.materialIds,
                  hasMaterials: cursorActions?.hasMaterials,
                  track: cursorActions?.track,
                  activityId: cursorActions?.activityId,
                  onlyFlagged: cursorActions?.onlyFlagged,
                },
              },
              fetchPolicy: 'network-only',
            });
          }

          const actionsResult = await selectActionOrders(
            data.data.actions.actions
          );

          const combinedDataNetwork = {
            actions: actionsResult,
            totalCount: data.data.actions.totalCount,
            pageInfo: data.data.actions.pageInfo,
            filtersDataOptions,
            roleActive,
          };

          return combinedDataNetwork;
        } finally {
          // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
          dispatch(loadingData(tableIds.REPORTING_ACTIONS, false));
        }
      }

      return combinedData;
    } catch (err) {
      console.log('Loader offline fallback:', err);
      // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
      store.dispatch(loadingData(tableIds.REPORTING_ACTIONS, false));
      return {
        actions: [],
        totalCount: 0,
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        filtersDataOptions: {
          capitols: [],
          concessions: [],
          subCapitols: [],
          classifications: [],
          externalCompanies: [],
          predefinedOrders: [],
          users: [],
        },
        roleActive: userData?.user?.user?.rolActive,
      };
    }
  };

  return { data: load() };
};
